import React, { useMemo } from "react";

import useTranslations from "../../hooks/useTranslations";

import { Tag } from "@chakra-ui/react";

import { formatDistanceToNow, isBefore } from "date-fns";
import { cloneDeep } from "lodash";
import useDateFnsLocale from "../../hooks/useDateFnsLocale";
import UserAvatar from "../utils/avatars/UserAvatar";
import HostAvatar from "../utils/avatars/HostAvatar";
import {
  ReviewFullFragment,
  ReviewReportFullFragment,
  UserMinimalFragment,
} from "../../graphql/generated/definitions";

type UserFilledActivityProps = {
  user: UserMinimalFragment;
  filledAt: string;
  isCollaborator?: boolean;
  hasNext?: boolean;
};

const UserFilledActivity: React.FC<UserFilledActivityProps> = ({
  user,
  filledAt,
  isCollaborator = false,
  hasNext = false,
}) => {
  const t = useTranslations();
  const dateFnsLocale = useDateFnsLocale();

  return (
    <li>
      <div className="relative pb-8">
        {hasNext && (
          <span
            className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-light-500"
            aria-hidden="true"
          ></span>
        )}
        <div className="relative flex items-start space-x-3">
          <div>
            <div className="relative px-1">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-light-0 ring-8 ring-white">
                <UserAvatar
                  size={user.avatar ? 8 : 5}
                  src={user.avatar}
                  alt={user.name}
                />
              </div>
            </div>
          </div>
          <div className="min-w-0 flex-1 py-1.5">
            <div className="flex flex-col space-x-1 text-sm text-gray-500 sm:items-center sm:flex-row">
              <div className="flex items-center space-x-1">
                <a href="#" className={`font-medium text-dark-100`}>
                  {user.name}
                </a>
                <span>{t("reviews.activities.hasFilled")}</span>
              </div>
              <div className="flex items-center pt-1 space-x-1 sm:pt-0">
                <span className="whitespace-nowrap">
                  {formatDistanceToNow(new Date(filledAt), {
                    addSuffix: true,
                    locale: dateFnsLocale,
                  })}
                </span>
                <Tag
                  scale="sm"
                  colorScheme={isCollaborator ? "blue" : "purple"}
                >
                  {isCollaborator
                    ? t("reviews.collaborator")
                    : t("reviews.referent", { count: 0 })}
                </Tag>
              </div>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

type UserNotFilledActivityProps = {
  review: ReviewFullFragment;
  report: ReviewReportFullFragment;
  user: UserMinimalFragment;
  isCollaborator?: boolean;
  hasNext?: boolean;
};

const UserNotFilledActivity: React.FC<UserNotFilledActivityProps> = ({
  review,
  report,
  user,
  isCollaborator = false,
  hasNext = false,
}) => {
  const t = useTranslations();

  return (
    <li>
      <div className="relative pb-8">
        {hasNext && (
          <span
            className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-light-500"
            aria-hidden="true"
          ></span>
        )}
        <div className="relative flex items-start space-x-3">
          <div>
            <div className="relative px-1">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-light-0 ring-8 ring-white">
                <UserAvatar
                  size={user.avatar ? 8 : 5}
                  src={user.avatar}
                  alt={user.name}
                />
              </div>
            </div>
          </div>
          <div className="min-w-0 flex-1 py-1.5">
            <div className="flex flex-col justify-between space-y-1 text-sm text-gray-500">
              <div className="flex items-center space-x-1 text-sm text-gray-500">
                <a href="#" className={"font-medium text-dark-100"}>
                  {user.name}
                </a>
                <span>{t("reviews.activities.awaiting")}</span>
                <Tag
                  scale="sm"
                  colorScheme={isCollaborator ? "blue" : "purple"}
                >
                  {isCollaborator
                    ? t("reviews.collaborator")
                    : t("reviews.referent", { count: 0 })}
                </Tag>
              </div>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

type ReportCheckedActivityProps = {
  review: ReviewFullFragment;
  report: ReviewReportFullFragment;
  hasNext?: boolean;
};

const ReportCheckedActivity: React.FC<ReportCheckedActivityProps> = ({
  review,
  report,
  hasNext,
}) => {
  const dateFnsLocale = useDateFnsLocale();
  const t = useTranslations();

  return (
    <>
      <li id={`report_${report.id}_checked_activity`}>
        <div className="relative pb-8">
          <span
            className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
            aria-hidden="true"
          ></span>
          <div className="relative flex items-start space-x-3">
            <HostAvatar
              reverse
              size={10}
              className="ring-8 ring-white"
              alt="Report"
            />
            <div className="flex-1 min-w-0">
              <div>
                <div className="text-sm">
                  <a href="#" className="font-medium text-dark-100">
                    {t(`reports.header.${report.type}`)} -{" "}
                    {review.template?.title}
                  </a>
                </div>
                <p className="mt-0.5 text-sm text-light-900">
                  {t("reviews.activities.completed")}{" "}
                  {formatDistanceToNow(new Date(report.checkedAt), {
                    addSuffix: true,
                    locale: dateFnsLocale,
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </li>
      {report.reportReferents.map((reportReferent) =>
        reportReferent.filledAt ? (
          <UserFilledActivity
            key={reportReferent.id}
            filledAt={reportReferent.filledAt}
            user={reportReferent.referent}
            hasNext={hasNext}
          />
        ) : (
          // this should not happen
          <UserNotFilledActivity
            key={reportReferent.id}
            user={reportReferent.referent}
            report={report}
            review={review}
            hasNext
          />
        )
      )}
      <UserFilledActivity
        isCollaborator
        filledAt={report.filledAt}
        user={review.collaborator}
        hasNext={hasNext}
      />
    </>
  );
};

type ReportUncheckedActivityProps = {
  review: ReviewFullFragment;
  report: ReviewReportFullFragment;
  hasNext?: boolean;
};

const ReportUncheckedActivity: React.FC<ReportUncheckedActivityProps> = ({
  review,
  report,
  hasNext = false,
}) => {
  const t = useTranslations();
  const dateFnsLocale = useDateFnsLocale();

  return (
    <>
      <li id={`report_${report.id}_checked_activity`}>
        <div className="relative pb-8">
          <span
            className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
            aria-hidden="true"
          ></span>
          <div className="relative flex items-start space-x-3">
            <HostAvatar
              reverse
              size={10}
              className="ring-8 ring-white"
              alt="Report"
            />
            <div className="flex-1 min-w-0">
              <div>
                <div className="text-sm">
                  <a href="#" className="font-medium text-dark-100">
                    {t(`reports.header.${report.type}`)} -{" "}
                    {review.template?.title}
                  </a>
                </div>
                <p className="mt-0.5 text-sm text-light-900">
                  {t("reviews.activities.toStart")}{" "}
                  {formatDistanceToNow(new Date(report.createdAt), {
                    addSuffix: true,
                    locale: dateFnsLocale,
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </li>
      {report.reportReferents.map((reportReferent) =>
        reportReferent.filledAt ? (
          <UserFilledActivity
            key={reportReferent.id}
            filledAt={reportReferent.filledAt}
            user={reportReferent.referent}
            hasNext={hasNext}
          />
        ) : (
          <UserNotFilledActivity
            key={reportReferent.id}
            user={reportReferent.referent}
            report={report}
            review={review}
            hasNext
          />
        )
      )}
      {report.filledAt ? (
        <UserFilledActivity
          isCollaborator
          filledAt={report.filledAt}
          user={review.collaborator}
          hasNext={hasNext}
        />
      ) : (
        <UserNotFilledActivity
          isCollaborator
          user={review.collaborator}
          hasNext={hasNext}
          report={report}
          review={review}
        />
      )}
    </>
  );
};

type UserFollowUpActivitiesProps = {
  review: ReviewFullFragment;
};

const UserFollowUpActivities: React.FC<UserFollowUpActivitiesProps> = ({
  review,
}) => {
  const t = useTranslations();

  const orderedReports = useMemo(() => {
    return cloneDeep(review.reports).sort((lhs, rhs) =>
      isBefore(new Date(lhs.createdAt), new Date(rhs.createdAt)) ? 1 : -1
    );
  }, [review]);

  if (!review) {
    return null;
  }

  return (
    <div className="flex flex-col">
      {orderedReports.length === 0 && (
        <div className="flex justify-center text-sm text-light-900">
          {t("reviews.noActivity")}
        </div>
      )}
      {orderedReports.length > 0 && (
        <ul className="-mb-8">
          {orderedReports.map((report, index) => {
            if (report.checkedAt) {
              return (
                <ReportCheckedActivity
                  key={report.id}
                  report={report}
                  review={review}
                  hasNext={index + 1 < orderedReports.length}
                />
              );
            }

            return (
              <ReportUncheckedActivity
                key={report.id}
                report={report}
                review={review}
                hasNext={index + 1 < orderedReports.length}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default UserFollowUpActivities;
